import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Button,
  Form,
  FormField,
  Input,
  TextArea,
  Dropdown,
  Message,
  Segment,
  Header,
  Checkbox,
  Grid,
  GridColumn,
  Confirm,
  Table,
  Modal,
  Icon
} from "semantic-ui-react";
import { LoadingWrapper } from "../../common";
import { fetchWithAuth, getCSRFToken, useAuth } from "../../auth";
import { formatPhoneNumber, validatePhoneNumber, getRawPhoneNumber, safeFormatPhoneNumber } from "../../../utils";
import SignatureCanvas from "react-signature-canvas";
import "react-datepicker/dist/react-datepicker.css";
import "../../../css/common.css";
import { formatDateToMmDdYyyy, formatDateToYyyyMmDd } from "../../../utils/formatDate";
import { useFuzzyAddressSearch } from "./useFuzzyAddressSearch";
import { SimilarAddressWarning } from "./SimilarAddressWarning";

export const JSAFormDetails = (props) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === undefined || id === 'new';
  const { isReadOnly } = useAuth();

  // State for all JSA forms (for fuzzy address search)
  const [allJSAForms, setAllJSAForms] = useState({});

  // Use fuzzy address search hook
  const { setSearchQuery, similarForms } = useFuzzyAddressSearch(allJSAForms, id);

  const [jsaForm, setJsaForm] = useState({
    date: formatDateToMmDdYyyy(new Date()),
    address: '',
    taskDescription: '',
    weather: '',
    foreman: null,
    foremanContact: '',
    nearestHospital: '',
    hospitalAddress: '',
    emergencyContact: '911',
    team: null,
    activities: '',
    potentialHazards: '',
    controlsAndBarriers: '',
    ppe: [],
    signatures: []
  });

  const [members, setMembers] = useState([]);
  const [teams, setTeams] = useState([]);
  const [ppeOptions, setPpeOptions] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [inputErrors, setInputErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [currentSignatureIndex, setCurrentSignatureIndex] = useState(null);
  const [signatureRef, setSignatureRef] = useState(null);
  const [newSigneeFirstName, setNewSigneeFirstName] = useState('');
  const [newSigneeLastName, setNewSigneeLastName] = useState('');
  const [pendingSignatureSave, setPendingSignatureSave] = useState(false);
  const [signaturesToDelete, setSignaturesToDelete] = useState([]);
  const [showPopup, setShowPopup] = useState(false);
  const [popupContent, setPopupContent] = useState("");
  const [popupError, setPopupError] = useState(false);

  // Fetch JSA form details if editing
  const fetchJSAForm = async () => {
    if (isNew) {
      return;
    }

    try {
      const response = await fetchWithAuth(
        process.env.REACT_APP_ICC_TOOL_API_HOST + `jsa/forms/${id}/`,
        {
          headers: { 'X-CSRFToken': getCSRFToken() },
          credentials: 'include'
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch JSA form');
      }

      const data = await response.json();
      setJsaForm({
        ...data,
        date: formatDateToMmDdYyyy(data.date),
        signatures: data.signatures || [],
        // Format phone numbers for display, handling invalid entries
        foremanContact: safeFormatPhoneNumber(data.foremanContact),
        emergencyContact: safeFormatPhoneNumber(data.emergencyContact)
      });
      setIsSubmitted(data.submissionTime !== null);
    } catch (err) {
      setShowPopup(true);
      setPopupError(true);
      setPopupContent('Failed to load JSA form details');
      console.error('Error fetching JSA form:', err);
    }
  };

  // Fetch members for foreman dropdown
  const fetchMembers = async () => {
    try {
      const response = await fetchWithAuth(
        process.env.REACT_APP_ICC_TOOL_API_HOST + 'user/team/members/',
        {
          headers: { 'X-CSRFToken': getCSRFToken() },
          credentials: 'include'
        }
      );

      if (response.ok) {
        const data = await response.json();
        const memberOptions = data.map(member => ({
          key: member.userId,
          value: member.userId,
          text: member.name
        }));
        setMembers(memberOptions);
      }
    } catch (err) {
      console.error('Error fetching members:', err);
    }
  };

  // Fetch teams
  const fetchTeams = async () => {
    try {
      const response = await fetchWithAuth(
        process.env.REACT_APP_ICC_TOOL_API_HOST + 'user/teams/',
        {
          headers: { 'X-CSRFToken': getCSRFToken() },
          credentials: 'include'
        }
      );

      if (response.ok) {
        const data = await response.json();
        const teamOptions = data.map(team => ({
          key: team.id,
          value: team.id,
          text: team.name
        }));
        setTeams(teamOptions);
      }
    } catch (err) {
      console.error('Error fetching teams:', err);
    }
  };

  // Fetch PPE options
  const fetchPPEOptions = async () => {
    try {
      const response = await fetchWithAuth(
        process.env.REACT_APP_ICC_TOOL_API_HOST + 'jsa/ppe/',
        {
          headers: { 'X-CSRFToken': getCSRFToken() },
          credentials: 'include'
        }
      );

      if (response.ok) {
        const data = await response.json();
        const ppeOpts = data.map(ppe => ({
          key: ppe.id,
          value: ppe.id,
          text: ppe.name
        }));
        setPpeOptions(ppeOpts);
      }
    } catch (err) {
      console.error('Error fetching PPE options:', err);
    }
  };

  // Fetch all JSA forms for address fuzzy search
  const fetchAllJSAForms = async () => {
    try {
      const response = await fetchWithAuth(
        process.env.REACT_APP_ICC_TOOL_API_HOST + 'jsa/forms/',
        {
          headers: { 'X-CSRFToken': getCSRFToken() },
          credentials: 'include'
        }
      );

      if (response.ok) {
        const data = await response.json();
        const forms = data.reduce((acc, form) => {
          acc[form.id] = form;
          return acc;
        }, {});
        setAllJSAForms(forms);
      }
    } catch (err) {
      console.error('Error fetching JSA forms for address search:', err);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      await Promise.all([
        fetchJSAForm(),
        fetchMembers(),
        fetchTeams(),
        fetchPPEOptions(),
        fetchAllJSAForms()
      ]);
      setLoaded(true);
    };
    loadData();
  }, [id]);

  // Trigger address search when form address is loaded or changes
  useEffect(() => {
    if (jsaForm.address && jsaForm.address.trim().length >= 5) {
      setSearchQuery(jsaForm.address);
    }
  }, [jsaForm.address, setSearchQuery]);

  const handleChange = (e, { name, value }) => {
    setJsaForm(prev => ({
      ...prev,
      [name]: value
    }));

    // Trigger fuzzy address search when address field changes
    if (name === 'address') {
      setSearchQuery(value);
    }

    // Clear error for this field
    if (inputErrors[name]) {
      setInputErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handlePhoneChange = (e, { name, value }) => {
    // Format the phone number as user types
    const formattedValue = formatPhoneNumber(value);

    setJsaForm(prev => ({
      ...prev,
      [name]: formattedValue
    }));

    // Clear error for this field
    if (inputErrors[name]) {
      setInputErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  const handleDateChange = (date) => {
    setJsaForm(prev => ({
      ...prev,
      date: date
    }));
  };

  const validateForm = () => {
    const errors = {};

    if (!jsaForm.date) errors.date = 'Date is required';
    if (!jsaForm.address.trim()) errors.address = 'Address is required';
    if (!jsaForm.taskDescription.trim()) errors.taskDescription = 'Task description is required';
    if (!jsaForm.weather.trim()) errors.weather = 'Weather is required';
    if (!jsaForm.foreman) errors.foreman = 'Supervisor/Foreman is required';
    if (!jsaForm.foremanContact.trim()) {
      errors.foremanContact = 'Foreman contact is required';
    } else if (!validatePhoneNumber(jsaForm.foremanContact)) {
      errors.foremanContact = 'Please enter a valid 10-digit US phone number';
    }
    if (!jsaForm.team) errors.team = 'Team is required';
    if (!jsaForm.nearestHospital.trim()) errors.nearestHospital = 'Nearest hospital is required';
    if (!jsaForm.hospitalAddress.trim()) errors.hospitalAddress = 'Hospital address is required';
    if (!jsaForm.emergencyContact.trim()) {
      errors.emergencyContact = 'Emergency contact is required';
    } else if (!validatePhoneNumber(jsaForm.emergencyContact)) {
      errors.emergencyContact = 'Please enter a valid 10-digit US phone number';
    }
    if (!jsaForm.activities.trim()) errors.activities = 'Activities are required';
    if (!jsaForm.potentialHazards.trim()) errors.potentialHazards = 'Potential hazards are required';
    if (!jsaForm.controlsAndBarriers.trim()) errors.controlsAndBarriers = 'Controls and barriers are required';

    setInputErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const saveForm = async (submit = false) => {
    if (!validateForm()) {
      setShowPopup(true);
      setPopupError(true);
      setPopupContent('Please fill in all required fields');
      return;
    }

    setSaving(true);

    try {
      // Only send new signatures (without a server-assigned id) in the payload;
      // existing signatures are kept by the backend unless listed in signatures_to_delete.
      const newSignatures = jsaForm.signatures.filter(sig => !sig.id);

      const formData = {
        ...jsaForm,
        date: formatDateToYyyyMmDd(jsaForm.date), // Database requires format date as YYYY-MM-DD
        submissionTime: submit ? new Date().toISOString() : null,
        // Save only raw phone numbers (digits only) to database
        foremanContact: getRawPhoneNumber(jsaForm.foremanContact),
        emergencyContact: getRawPhoneNumber(jsaForm.emergencyContact),
        signatures: newSignatures,
        ...(signaturesToDelete.length > 0 && { signatures_to_delete: signaturesToDelete })
      };

      const url = isNew
        ? process.env.REACT_APP_ICC_TOOL_API_HOST + 'jsa/forms/'
        : process.env.REACT_APP_ICC_TOOL_API_HOST + `jsa/forms/${id}/`;

      const method = isNew ? 'POST' : 'PATCH';

      const response = await fetchWithAuth(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCSRFToken()
        },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to save JSA form');
      }

      const savedForm = await response.json();

      // Sync signatures (and any other server-assigned fields) back into local
      // state so that subsequent PUT requests include the server-assigned `id`
      // on each already-saved signature.  Without this, every save would send
      // signatures without `id`, causing the backend to recreate them and reset
      // their timestamps.
      setJsaForm(prev => ({
        ...prev,
        signatures: savedForm.signatures || prev.signatures
      }));
      setSignaturesToDelete([]);

      if (submit) {
        setShowPopup(true);
        setPopupError(false);
        setPopupContent('JSA form submitted successfully!');
        setIsSubmitted(true);
      } else {
        setShowPopup(true);
        setPopupError(false);
        setPopupContent('JSA form saved as draft successfully!');
      }

      if (isNew) {
        navigate(`/forms/jsaform/${savedForm.id}`);
      }
    } catch (err) {
      setShowPopup(true);
      setPopupError(true);
      setPopupContent(err.message || 'Failed to save JSA form');
      console.error('Error saving JSA form:', err);
    }

    setSaving(false);
  };

  // Signature management functions
  const handleAddSignature = () => {
    setCurrentSignatureIndex(null);
    setNewSigneeFirstName('');
    setNewSigneeLastName('');
    setShowSignatureModal(true);

    // Clear the signature canvas when adding new signature
    setTimeout(() => {
      if (signatureRef) {
        signatureRef.clear();
      }
    }, 100);
  };

  const handleEditSignature = (index) => {
    setCurrentSignatureIndex(index);
    // Use the separate first and last name fields from the API
    setNewSigneeFirstName(jsaForm.signatures[index].signeeFirstName || '');
    setNewSigneeLastName(jsaForm.signatures[index].signeeLastName || '');
    setShowSignatureModal(true);

    // Pre-populate the signature canvas when modal opens
    setTimeout(() => {
      if (signatureRef && jsaForm.signatures[index].signatureEncodedImage) {
        signatureRef.fromDataURL(jsaForm.signatures[index].signatureEncodedImage);
      }
    }, 100);
  };

  const handleDeleteSignature = (index) => {
    const signature = jsaForm.signatures[index];
    // If the signature already exists on the server (has an id), queue it for deletion
    if (signature.id) {
      setSignaturesToDelete(prev => [...prev, signature.id]);
    }
    setJsaForm(prev => ({
      ...prev,
      signatures: prev.signatures.filter((_, i) => i !== index)
    }));
  };

  const handleSaveSignature = () => {
    if (!newSigneeFirstName.trim()) {
      setShowPopup(true);
      setPopupError(true);
      setPopupContent('First name is required');
      return;
    }

    if (!newSigneeLastName.trim()) {
      setShowPopup(true);
      setPopupError(true);
      setPopupContent('Last name is required');
      return;
    }

    if (!signatureRef || signatureRef.isEmpty()) {
      setShowPopup(true);
      setPopupError(true);
      setPopupContent('Signature is required');
      return;
    }

    const signatureData = signatureRef.toDataURL();
    const currentTime = new Date().toISOString();

    const newSignature = {
      signeeFirstName: newSigneeFirstName.trim(),
      signeeLastName: newSigneeLastName.trim(),
      signatureEncodedImage: signatureData,
      modifiedTime: currentTime
    };

    if (currentSignatureIndex !== null) {
      // Edit existing signature
      const updatedSignatures = [...jsaForm.signatures];
      updatedSignatures[currentSignatureIndex] = {
        ...updatedSignatures[currentSignatureIndex],
        signeeFirstName: newSigneeFirstName.trim(),
        signeeLastName: newSigneeLastName.trim(),
        signatureEncodedImage: signatureData,
        modifiedTime: currentTime
      };
      setJsaForm(prev => ({
        ...prev,
        signatures: updatedSignatures
      }));
    } else {
      // Add new signature
      setJsaForm(prev => ({
        ...prev,
        signatures: [...prev.signatures, newSignature]
      }));
    }

    setShowSignatureModal(false);
    setNewSigneeFirstName('');
    setNewSigneeLastName('');

    // Set flag to trigger save after state update
    setPendingSignatureSave(true);
  };

  useEffect(() => {
    if (pendingSignatureSave) {
      saveForm(false);
      setPendingSignatureSave(false);
    }
  }, [jsaForm.signatures, pendingSignatureSave]);

  const handleCancelSignature = () => {
    setShowSignatureModal(false);
    setNewSigneeFirstName('');
    setNewSigneeLastName('');
    if (signatureRef) {
      signatureRef.clear();
    }
  };

  const clearSignature = () => {
    if (signatureRef) {
      signatureRef.clear();
    }
  };

  const saveDraft = () => saveForm(false);

  const handleSubmitClick = () => {
    setShowConfirm(true);
  };

  const handleConfirmSubmit = () => {
    setShowConfirm(false);
    saveForm(true);
  };

  const handleCancelSubmit = () => {
    setShowConfirm(false);
  };

  return (
    <LoadingWrapper loading={!loaded} text="Loading JSA form">
    <div style={{ maxWidth: '1000px', margin: '0 auto' }}>

      <Header as="h2">
        {isNew ? 'Create New JSA Form' : `JSA Form - ${jsaForm.taskDescription || 'Untitled'}`}
        {isSubmitted && <span style={{ color: 'green', marginLeft: '10px' }}>(Submitted)</span>}
      </Header>

      <Form>
        <Segment>
          <Header as="h4">Basic Information</Header>
          <Grid>
            <GridColumn width={8}>
              <FormField required error={!!inputErrors.date}>
                <label>Date</label>
                <Input
                  name="date"
                  value={jsaForm.date}
                  onChange={handleDateChange}
                  readOnly={true}
                />
                {inputErrors.date && <div style={{ color: 'red' }}>{inputErrors.date}</div>}
              </FormField>
            </GridColumn>
            <GridColumn width={8}>
              <FormField required error={!!inputErrors.weather}>
                <label>Weather</label>
                <Input
                  name="weather"
                  value={jsaForm.weather}
                  onChange={handleChange}
                  disabled={isSubmitted}
                  placeholder="e.g., Sunny, 22°C"
                />
                {inputErrors.weather && <div style={{ color: 'red' }}>{inputErrors.weather}</div>}
              </FormField>
            </GridColumn>
          </Grid>

          <FormField required error={!!inputErrors.address}>
            <label>Address</label>
            <Input
              name="address"
              value={jsaForm.address}
              onChange={handleChange}
              disabled={isSubmitted}
              placeholder="Job site address"
            />
            {inputErrors.address && <div style={{ color: 'red' }}>{inputErrors.address}</div>}
          </FormField>

          <SimilarAddressWarning similarForms={similarForms} />

          <FormField required error={!!inputErrors.taskDescription}>
            <label>Description of Task to be Performed</label>
            <TextArea
              name="taskDescription"
              value={jsaForm.taskDescription}
              onChange={handleChange}
              disabled={isSubmitted}
              placeholder="Describe the task to be performed"
              rows={3}
            />
            {inputErrors.taskDescription && <div style={{ color: 'red' }}>{inputErrors.taskDescription}</div>}
          </FormField>

          <Grid>
            <GridColumn width={8}>
              <FormField required error={!!inputErrors.foreman}>
                <label>Supervisor/Foreman</label>
                <Dropdown
                  placeholder="Select Supervisor/Foreman"
                  fluid
                  search
                  selection
                  name="foreman"
                  value={jsaForm.foreman}
                  options={members}
                  onChange={handleChange}
                  disabled={isSubmitted}
                />
                {inputErrors.foreman && <div style={{ color: 'red' }}>{inputErrors.foreman}</div>}
              </FormField>
            </GridColumn>
            <GridColumn width={8}>
              <FormField required error={!!inputErrors.foremanContact}>
                <label>Contact Number</label>
                <Input
                  name="foremanContact"
                  value={jsaForm.foremanContact}
                  onChange={handlePhoneChange}
                  disabled={isSubmitted}
                  placeholder="(555) 123-4567"
                />
                {inputErrors.foremanContact && <div style={{ color: 'red' }}>{inputErrors.foremanContact}</div>}
              </FormField>
            </GridColumn>
            <GridColumn width={8}>
              <FormField required error={!!inputErrors.team}>
                <label>Team</label>
                <Dropdown
                  placeholder="Select team"
                  fluid
                  search
                  selection
                  name="team"
                  value={jsaForm.team}
                  options={teams}
                  onChange={handleChange}
                  disabled={isSubmitted}
                />
                {inputErrors.team && <div style={{ color: 'red' }}>{inputErrors.team}</div>}
              </FormField>
            </GridColumn>
          </Grid>
        </Segment>

        <Segment>
          <Header as="h4">Location Information</Header>
          <Grid>
            <GridColumn width={8}>
              <FormField required error={!!inputErrors.nearestHospital}>
                <label>Nearest Hospital</label>
                <Input
                  name="nearestHospital"
                  value={jsaForm.nearestHospital}
                  onChange={handleChange}
                  disabled={isSubmitted}
                  placeholder="Nearest hospital name and address"
                />
                {inputErrors.nearestHospital && <div style={{ color: 'red' }}>{inputErrors.nearestHospital}</div>}
              </FormField>
            </GridColumn>

            <GridColumn width={8}>
              <FormField required error={!!inputErrors.hospitalAddress}>
                <label>Hospital Address</label>
                <Input
                  name="hospitalAddress"
                  value={jsaForm.hospitalAddress}
                  onChange={handleChange}
                  disabled={isSubmitted}
                  placeholder="Nearest hospital name and address"
                />
                {inputErrors.hospitalAddress && <div style={{ color: 'red' }}>{inputErrors.hospitalAddress}</div>}
              </FormField>
            </GridColumn>

            <GridColumn width={8}>
              <FormField required error={!!inputErrors.emergencyContact}>
                <label>Emergency Contact</label>
                <Input
                  name="emergencyContact"
                  value={jsaForm.emergencyContact}
                  onChange={handlePhoneChange}
                  disabled={isSubmitted}
                  placeholder="911 or (555) 123-4567"
                />
                {inputErrors.emergencyContact && <div style={{ color: 'red' }}>{inputErrors.emergencyContact}</div>}
              </FormField>
            </GridColumn>
          </Grid>
        </Segment>

        <Segment>
          <Header as="h4">Safety Analysis</Header>
          <FormField required error={!!inputErrors.activities}>
            <label>Activities</label>
            <TextArea
              name="activities"
              value={jsaForm.activities}
              onChange={handleChange}
              disabled={isSubmitted}
              placeholder="List all activities to be performed"
              rows={4}
            />
            {inputErrors.activities && <div style={{ color: 'red' }}>{inputErrors.activities}</div>}
          </FormField>

          <FormField required error={!!inputErrors.potentialHazards}>
            <label>Potential Hazards</label>
            <TextArea
              name="potentialHazards"
              value={jsaForm.potentialHazards}
              onChange={handleChange}
              disabled={isSubmitted}
              placeholder="Identify potential hazards"
              rows={4}
            />
            {inputErrors.potentialHazards && <div style={{ color: 'red' }}>{inputErrors.potentialHazards}</div>}
          </FormField>

          <FormField required error={!!inputErrors.controlsAndBarriers}>
            <label>Controls and Barriers</label>
            <TextArea
              name="controlsAndBarriers"
              value={jsaForm.controlsAndBarriers}
              onChange={handleChange}
              disabled={isSubmitted}
              placeholder="Describe controls and barriers to mitigate hazards"
              rows={4}
            />
            {inputErrors.controlsAndBarriers && <div style={{ color: 'red' }}>{inputErrors.controlsAndBarriers}</div>}
          </FormField>

          <FormField>
            <label>Required Personal Protective Equipment (PPE)</label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px', marginTop: '10px' }}>
              {ppeOptions.map(ppe => (
                <Checkbox
                  key={ppe.key}
                  label={ppe.text}
                  checked={jsaForm.ppe.includes(ppe.value)}
                  onChange={(e, { checked }) => {
                    const updatedPpe = checked
                      ? [...jsaForm.ppe, ppe.value]
                      : jsaForm.ppe.filter(id => id !== ppe.value);

                    setJsaForm(prev => ({
                      ...prev,
                      ppe: updatedPpe
                    }));
                  }}
                  disabled={isSubmitted}
                  style={{ textAlign: 'left', justifyContent: 'flex-start' }}
                />
              ))}
            </div>
          </FormField>

          <FormField>

            <TextArea
              name="ppeOthers"
              value={jsaForm.ppeOthers}
              onChange={handleChange}
              disabled={isSubmitted}
              placeholder="Describe any other required PPE"
              rows={4}
            />
          </FormField>
        </Segment>

        <Segment>
          <Header as="h4">Signatures</Header>

          <div style={{
            backgroundColor: '#f8f9fa',
            padding: '15px',
            borderRadius: '5px',
            marginBottom: '20px',
            border: '1px solid #dee2e6'
          }}>
            <p style={{
              margin: 0,
              fontSize: '14px',
              lineHeight: '1.5',
              fontStyle: 'italic',
              color: '#333'
            }}>
              <strong>Disclaimer:</strong> I understand & will adhere to the steps, hazards & controls as described in this JSA.
              I understand that performing steps out of sequence may pose hazards that have not been evaluated, nor authorized.
              I will contact my supervisor prior to continuing work, if the scope of work changes or new hazards are introduced.
              I understand I have the authority and responsibility to stop work I believe to be unsafe.
            </p>
          </div>

          <Table celled>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Signee Name</Table.HeaderCell>
                <Table.HeaderCell>Signature</Table.HeaderCell>
                <Table.HeaderCell>Date/Time</Table.HeaderCell>
                {!isSubmitted && !isReadOnly && <Table.HeaderCell>Actions</Table.HeaderCell>}
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {jsaForm.signatures.map((signature, index) => (
                <Table.Row key={index}>
                  <Table.Cell>{`${signature.signeeFirstName || ''} ${signature.signeeLastName || ''}`.trim()}</Table.Cell>
                  <Table.Cell>
                    {signature.signatureEncodedImage && (
                      <img
                        src={signature.signatureEncodedImage}
                        alt="Signature"
                        style={{ maxWidth: '200px', maxHeight: '60px', border: '1px solid #ccc' }}
                      />
                    )}
                  </Table.Cell>
                  <Table.Cell>
                    {signature.modifiedTime ? new Date(signature.modifiedTime).toLocaleString() : 'Not set'}
                  </Table.Cell>
                  {!isSubmitted && !isReadOnly && (
                    <Table.Cell>
                      <Button
                        size="mini"
                        onClick={() => handleEditSignature(index)}
                        icon="edit"
                        title="Edit signature"
                      />
                      <Button
                        size="mini"
                        color="red"
                        onClick={() => handleDeleteSignature(index)}
                        icon="delete"
                        title="Delete signature"
                        style={{ marginLeft: '5px' }}
                      />
                    </Table.Cell>
                  )}
                </Table.Row>
              ))}
              {!isSubmitted && !isReadOnly && (
                <Table.Row>
                  <Table.Cell colSpan={4} textAlign="center">
                    <Button
                      onClick={handleAddSignature}
                      color="green"
                      size="small"
                    >
                      <Icon name="plus" />
                      Add Signature
                    </Button>
                  </Table.Cell>
                </Table.Row>
              )}
            </Table.Body>
          </Table>
        </Segment>

        <Segment>
          <Header as="h4">Audit Information</Header>
          <Grid>
            <GridColumn width={8}>
              <FormField>
                <label>Created By</label>
                <Input
                  value={jsaForm.createdBy || 'N/A'}
                  readOnly
                  placeholder="Not available"
                />
              </FormField>
            </GridColumn>
            <GridColumn width={8}>
              <FormField>
                <label>Creation Time</label>
                <Input
                  value={jsaForm.creationTime ? new Date(jsaForm.creationTime).toLocaleString() : 'N/A'}
                  readOnly
                  placeholder="Not available"
                />
              </FormField>
            </GridColumn>
            <GridColumn width={8}>
              <FormField>
                <label>Updated By</label>
                <Input
                  value={jsaForm.updatedBy || 'N/A'}
                  readOnly
                  placeholder="Not available"
                />
              </FormField>
            </GridColumn>
            <GridColumn width={8}>
              <FormField>
                <label>Modified Time</label>
                <Input
                  value={jsaForm.modifiedTime ? new Date(jsaForm.modifiedTime).toLocaleString() : 'N/A'}
                  readOnly
                  placeholder="Not available"
                />
              </FormField>
            </GridColumn>
          </Grid>
        </Segment>

        {!isSubmitted && !isReadOnly && (
          <div style={{ marginTop: '20px' }}>
            <Button
              type="button"
              onClick={saveDraft}
              loading={saving}
              disabled={saving}
              style={{ marginRight: '10px' }}
              color="green"
            >
              Save as Draft
            </Button>
            <Button
              type="button"
              onClick={() => navigate('/forms/jsaform')}
              disabled={saving}
              style={{ marginLeft: '10px' }}
            >
              Cancel
            </Button>
          </div>
        )}

        {isSubmitted && (
          <div style={{ marginTop: '20px' }}>
            <Button
              type="button"
              onClick={() => navigate('/forms/jsaform')}
            >
              Back to JSA List
            </Button>
          </div>
        )}

        <div style={{ height: '40px' }}></div>
      </Form>

      <Confirm
        open={showConfirm}
        header="Submit JSA Form"
        content="Submitting this form will finalize it for the day and it cannot be retrieved for additional signatures or edits. Are you sure you want to proceed?"
        confirmButton="Yes, Submit"
        cancelButton="Cancel"
        onConfirm={handleConfirmSubmit}
        onCancel={handleCancelSubmit}
      />

      <Modal
        open={showSignatureModal}
        onClose={handleCancelSignature}
        size="small"
      >
        <Header content={currentSignatureIndex !== null ? "Edit Signature" : "Add Signature"} />
        <Modal.Content>
          <Form>
            <Grid>
              <GridColumn width={8}>
                <FormField required>
                  <label>First Name</label>
                  <Input
                    value={newSigneeFirstName}
                    onChange={(e) => setNewSigneeFirstName(e.target.value)}
                    placeholder="Enter first name"
                  />
                </FormField>
              </GridColumn>
              <GridColumn width={8}>
                <FormField required>
                  <label>Last Name</label>
                  <Input
                    value={newSigneeLastName}
                    onChange={(e) => setNewSigneeLastName(e.target.value)}
                    placeholder="Enter last name"
                  />
                </FormField>
              </GridColumn>
            </Grid>

            <FormField required>
              <label>Signature</label>
              <div style={{
                position: 'relative',
                border: '1px solid #ccc',
                borderRadius: '4px',
                backgroundColor: 'white'
              }}>
                <SignatureCanvas
                  ref={setSignatureRef}
                  penColor="black"
                  canvasProps={{
                    width: 450,
                    height: 150,
                    className: 'signature-canvas',
                    style: { border: 'none', display: 'block' }
                  }}
                />
                <Button
                  type="button"
                  onClick={clearSignature}
                  size="tiny"
                  style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    zIndex: 2
                  }}
                >
                  Clear
                </Button>
              </div>
            </FormField>
          </Form>
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={handleCancelSignature}>Cancel</Button>
          <Button
            positive
            onClick={handleSaveSignature}
          >
            Save
          </Button>
        </Modal.Actions>
      </Modal>

      {showPopup && (
        <div className="popup-overlay">
          <div className="popup-box">
            <Message
              positive={!popupError && !!popupContent}
              negative={!!popupError}
              style={{ marginBottom: 20, width: '100%' }}
            >
              {popupContent}
            </Message>
            <Button
              size='large'
              color={popupError ? 'grey' : 'green'}
              onClick={() => setShowPopup(false)}
            >
              OK
            </Button>
          </div>
        </div>
      )}
    </div>
    </LoadingWrapper>
  );
};
